<template>
  <div class="operator-settings">
    <el-alert
      v-if="accessError"
      class="access-alert"
      type="warning"
      :closable="false"
      show-icon
      title="当前账号没有 TikHub 运营代理管理权限"
      :description="accessError"
    />

    <template v-else>
      <div class="summary-grid">
        <div class="summary-item">
          <div class="summary-label">伙伴数</div>
          <div class="summary-value">{{ partnerSummary.length }}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">运营账号</div>
          <div class="summary-value">{{ callers.length }}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">已启用账号</div>
          <div class="summary-value">{{ enabledCallerCount }}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">当前累计调用</div>
          <div class="summary-value">{{ totalChargedCount }}</div>
        </div>
      </div>

      <el-tabs v-model="section" class="operator-tabs">
        <el-tab-pane label="运营账号与权限" name="callers">
          <div class="toolbar">
            <div class="filters">
              <el-input
                v-model.trim="callerFilter.partnerId"
                clearable
                placeholder="伙伴ID"
                class="filter-partner"
                @keyup.enter.native="loadCallers"
              />
              <el-select v-model="callerFilter.enabled" clearable placeholder="全部状态" class="filter-status">
                <el-option label="已启用" :value="true"/>
                <el-option label="已停用" :value="false"/>
              </el-select>
              <el-button :loading="callerLoading" @click="loadCallers">查询</el-button>
            </div>
            <el-button type="primary" icon="el-icon-plus" @click="openCreateCaller">新增运营账号</el-button>
          </div>

          <el-table
            v-loading="callerLoading"
            :data="callers"
            empty-text="当前筛选条件下没有运营账号"
          >
            <el-table-column label="运营账号" min-width="180">
              <template slot-scope="{ row }">
                <div class="primary-text">{{ row.userName }}</div>
                <div class="secondary-text">{{ row.userId }}</div>
              </template>
            </el-table-column>
            <el-table-column label="所属伙伴" min-width="160">
              <template slot-scope="{ row }">
                <div class="primary-text">{{ row.partnerName }}</div>
                <div class="secondary-text">{{ row.partnerId }}</div>
              </template>
            </el-table-column>
            <el-table-column label="授权平台" min-width="250">
              <template slot-scope="{ row }">
                <el-tag
                  v-for="platform in row.authorizedPlatforms"
                  :key="platform"
                  size="mini"
                  class="platform-tag"
                >{{ platformLabel(platform) }}</el-tag>
                <span v-if="!row.authorizedPlatforms || !row.authorizedPlatforms.length" class="secondary-text">未授权平台</span>
              </template>
            </el-table-column>
            <el-table-column label="今日额度" width="130">
              <template slot-scope="{ row }">
                {{ usageText(row.dailyUsedCount, row.dailyReservedCount, row.dailyLimit) }}
              </template>
            </el-table-column>
            <el-table-column label="累计额度" width="150">
              <template slot-scope="{ row }">
                {{ usageText(row.totalUsedCount, row.totalReservedCount, row.totalLimit) }}
              </template>
            </el-table-column>
            <el-table-column label="状态" width="90">
              <template slot-scope="{ row }">
                <el-tag :type="row.enabled ? 'success' : 'info'" size="small">
                  {{ row.enabled ? '已启用' : '已停用' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="150" fixed="right">
              <template slot-scope="{ row }">
                <el-button type="text" @click="openEditCaller(row)">编辑</el-button>
                <el-button type="text" @click="resetQuota(row)">重置额度</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>

        <el-tab-pane label="伙伴汇总" name="partners">
          <div class="toolbar">
            <div class="filters">
              <el-date-picker
                v-model="partnerDateRange"
                type="datetimerange"
                value-format="yyyy-MM-dd HH:mm:ss"
                start-placeholder="开始时间"
                end-placeholder="结束时间"
                range-separator="至"
                class="date-range"
              />
              <el-button :loading="partnerLoading" @click="loadPartners">查询</el-button>
            </div>
          </div>
          <el-table
            v-loading="partnerLoading"
            :data="partnerSummary"
            empty-text="还没有配置伙伴和运营账号"
          >
            <el-table-column label="伙伴" min-width="180">
              <template slot-scope="{ row }">
                <div class="primary-text">{{ row.partnerName }}</div>
                <div class="secondary-text">{{ row.partnerId }}</div>
              </template>
            </el-table-column>
            <el-table-column prop="operatorCount" label="运营账号" width="100"/>
            <el-table-column prop="enabledOperatorCount" label="已启用" width="90"/>
            <el-table-column prop="chargedCount" label="调用次数" width="110"/>
            <el-table-column prop="successCount" label="成功" width="90"/>
            <el-table-column prop="failedCount" label="失败" width="90"/>
            <el-table-column prop="timeoutCount" label="超时" width="90"/>
            <el-table-column label="当前累计用量" width="150">
              <template slot-scope="{ row }">
                {{ number(row.currentTotalUsedCount) }}
                <span v-if="number(row.currentTotalReservedCount)" class="reserved">
                  +{{ number(row.currentTotalReservedCount) }}预占
                </span>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="150" fixed="right">
              <template slot-scope="{ row }">
                <el-button
                  v-if="number(row.enabledOperatorCount) > 0"
                  type="text"
                  class="danger-action"
                  @click="setPartnerEnabled(row, false)"
                >停用伙伴</el-button>
                <el-button
                  v-else
                  type="text"
                  @click="setPartnerEnabled(row, true)"
                >启用伙伴</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>

        <el-tab-pane label="调用记录" name="records">
          <div class="record-filters">
            <el-input v-model.trim="recordFilter.userId" clearable placeholder="运营userId"/>
            <el-input v-model.trim="recordFilter.partnerId" clearable placeholder="伙伴ID"/>
            <el-select v-model="recordFilter.platformCode" clearable placeholder="全部平台">
              <el-option
                v-for="platform in platformOptions"
                :key="platform.code"
                :label="platform.name"
                :value="platform.code"
              />
            </el-select>
            <el-select
              v-model="recordFilter.operationCode"
              clearable
              filterable
              placeholder="全部接口能力"
              class="operation-filter"
            >
              <el-option
                v-for="endpoint in filteredEndpointOptions"
                :key="endpoint.operationCode"
                :label="endpoint.operationName || endpoint.operationCode"
                :value="endpoint.operationCode"
              />
            </el-select>
            <el-select v-model="recordFilter.resultStatus" clearable placeholder="全部结果">
              <el-option label="成功" value="SUCCESS"/>
              <el-option label="失败" value="FAILED"/>
              <el-option label="超时" value="TIMEOUT"/>
            </el-select>
            <el-date-picker
              v-model="recordDateRange"
              type="datetimerange"
              value-format="yyyy-MM-dd HH:mm:ss"
              start-placeholder="开始时间"
              end-placeholder="结束时间"
              range-separator="至"
            />
            <el-button type="primary" :loading="recordLoading" @click="loadRecords">查询</el-button>
          </div>
          <el-table
            v-loading="recordLoading"
            :data="records"
            empty-text="当前筛选条件下没有调用记录"
          >
            <el-table-column prop="startedAt" label="调用时间" width="170">
              <template slot-scope="{ row }">{{ dateTime(row.startedAt) }}</template>
            </el-table-column>
            <el-table-column label="运营/伙伴" min-width="180">
              <template slot-scope="{ row }">
                <div class="primary-text">{{ row.userName || row.userId }}</div>
                <div class="secondary-text">{{ row.partnerId }}</div>
              </template>
            </el-table-column>
            <el-table-column label="平台" width="100">
              <template slot-scope="{ row }">{{ platformLabel(row.platformCode) }}</template>
            </el-table-column>
            <el-table-column prop="operationCode" label="接口能力" min-width="210" show-overflow-tooltip/>
            <el-table-column label="结果" width="90">
              <template slot-scope="{ row }">
                <el-tag :type="resultType(row.resultStatus)" size="small">
                  {{ resultLabel(row.resultStatus) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="tikhubHttpStatus" label="HTTP" width="74"/>
            <el-table-column prop="chargedCount" label="计次" width="68"/>
            <el-table-column label="耗时" width="90">
              <template slot-scope="{ row }">{{ number(row.elapsedMs) }}ms</template>
            </el-table-column>
            <el-table-column prop="requestId" label="requestId" min-width="250" show-overflow-tooltip/>
          </el-table>
        </el-tab-pane>

        <el-tab-pane label="代理接口状态" name="endpoints">
          <el-alert
            class="endpoint-note"
            type="info"
            :closable="false"
            show-icon
            title="运营账号按平台授权；这里仅控制22项代理接口是否已完成真实验证并可对外使用。"
          />
          <el-table
            v-loading="endpointLoading"
            :data="endpoints"
            empty-text="没有读取到代理接口目录"
          >
            <el-table-column label="平台" width="105">
              <template slot-scope="{ row }">{{ platformLabel(row.platformCode) }}</template>
            </el-table-column>
            <el-table-column prop="operationName" label="接口名称" min-width="170"/>
            <el-table-column prop="operationCode" label="能力编码" min-width="220" show-overflow-tooltip/>
            <el-table-column label="验证状态" width="120">
              <template slot-scope="{ row }">
                <el-tag :type="liveStatusType(row.liveStatus)" size="small">
                  {{ liveStatusLabel(row.liveStatus) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="启用状态" width="100">
              <template slot-scope="{ row }">
                <el-switch
                  :value="Boolean(row.enabled)"
                  :disabled="row.liveStatus !== 'LIVE_VERIFIED' || endpointSaving === row.operationCode"
                  @change="updateEndpoint(row, { enabled: $event })"
                />
              </template>
            </el-table-column>
            <el-table-column label="操作" width="190" fixed="right">
              <template slot-scope="{ row }">
                <el-dropdown
                  :disabled="endpointSaving === row.operationCode"
                  trigger="click"
                  @command="command => updateEndpoint(row, { live_status: command })"
                >
                  <el-button type="text" :loading="endpointSaving === row.operationCode">
                    更新验证状态<i class="el-icon-arrow-down el-icon--right"/>
                  </el-button>
                  <el-dropdown-menu slot="dropdown">
                    <el-dropdown-item command="LIVE_VERIFIED">标记验证通过</el-dropdown-item>
                    <el-dropdown-item command="LIVE_FAILED">标记验证失败</el-dropdown-item>
                    <el-dropdown-item command="PENDING">恢复待验证</el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>
      </el-tabs>
    </template>

    <el-dialog
      :title="editingCallerId ? '编辑运营账号与权限' : '新增运营账号'"
      :visible.sync="callerDialogVisible"
      width="720px"
      :close-on-click-modal="false"
      :show-close="!callerSaving"
      @closed="resetCallerForm"
    >
      <el-form
        ref="callerForm"
        :model="callerForm"
        :rules="callerRules"
        label-width="104px"
        :disabled="callerSaving"
      >
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="运营userId" prop="userId">
              <el-input
                v-model.trim="callerForm.userId"
                :disabled="Boolean(editingCallerId)"
                maxlength="64"
                placeholder="API Key映射得到的登录userId"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="运营姓名" prop="userName">
              <el-input v-model.trim="callerForm.userName" maxlength="128" placeholder="运营人员姓名"/>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="伙伴ID" prop="partnerId">
              <el-input v-model.trim="callerForm.partnerId" maxlength="64" placeholder="伙伴唯一编码"/>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="伙伴名称" prop="partnerName">
              <el-input v-model.trim="callerForm.partnerName" maxlength="128" placeholder="伙伴名称"/>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="授权平台" prop="authorizedPlatforms">
          <el-checkbox-group v-model="callerForm.authorizedPlatforms">
            <el-checkbox
              v-for="platform in platformOptions"
              :key="platform.code"
              :label="platform.code"
            >{{ platform.name }}</el-checkbox>
          </el-checkbox-group>
          <div class="form-help">按平台授权，平台下已启用的接口均可调用。</div>
        </el-form-item>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="每日上限" prop="dailyLimit">
              <el-input-number
                v-model="callerForm.dailyLimit"
                :min="0"
                :max="2147483647"
                controls-position="right"
                class="full-width"
              />
              <div class="form-help">0表示不限次数。</div>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="累计上限" prop="totalLimit">
              <el-input-number
                v-model="callerForm.totalLimit"
                :min="0"
                :max="9007199254740991"
                controls-position="right"
                class="full-width"
              />
              <div class="form-help">从首次授权或最近一次重置起累计；0表示不限。</div>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="账号状态">
          <el-switch
            v-model="callerForm.enabled"
            active-text="启用"
            inactive-text="停用"
          />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button :disabled="callerSaving" @click="callerDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="callerSaving" @click="saveCaller">
          {{ callerSaving ? '保存中' : '保存' }}
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { yuqingApi } from '@/api/yuqing'

const ADMIN_BASE = 'tikhub/operator/admin'
const PLATFORM_NAMES = {
  bilibili: 'B站',
  douyin: '抖音',
  kuaishou: '快手',
  wechat_mp: '微信公众号',
  weibo: '微博',
  xiaohongshu: '小红书',
  zhihu: '知乎'
}

const emptyCaller = () => ({
  userId: '',
  userName: '',
  partnerId: '',
  partnerName: '',
  authorizedPlatforms: [],
  dailyLimit: 0,
  totalLimit: 0,
  enabled: true
})

export default {
  data () {
    return {
      section: 'callers',
      accessError: '',
      callers: [],
      partnerSummary: [],
      records: [],
      endpoints: [],
      callerLoading: false,
      partnerLoading: false,
      recordLoading: false,
      endpointLoading: false,
      callerSaving: false,
      endpointSaving: '',
      callerFilter: { partnerId: '', enabled: '' },
      partnerDateRange: [],
      recordDateRange: [],
      recordFilter: {
        userId: '',
        partnerId: '',
        platformCode: '',
        operationCode: '',
        resultStatus: ''
      },
      callerDialogVisible: false,
      editingCallerId: null,
      callerForm: emptyCaller(),
      callerRules: {
        userId: [{ required: true, message: '请输入运营userId', trigger: 'blur' }],
        userName: [{ required: true, message: '请输入运营姓名', trigger: 'blur' }],
        partnerId: [{ required: true, message: '请输入伙伴ID', trigger: 'blur' }],
        partnerName: [{ required: true, message: '请输入伙伴名称', trigger: 'blur' }],
        authorizedPlatforms: [{
          type: 'array',
          required: true,
          min: 1,
          message: '请至少授权一个平台',
          trigger: 'change'
        }]
      }
    }
  },
  computed: {
    enabledCallerCount () {
      return this.callers.filter(item => item.enabled).length
    },
    totalChargedCount () {
      return this.partnerSummary.reduce((total, item) => total + this.number(item.chargedCount), 0)
    },
    platformOptions () {
      const codes = new Set(Object.keys(PLATFORM_NAMES))
      this.endpoints.forEach(item => {
        if (item.platformCode) codes.add(item.platformCode)
      })
      return Array.from(codes)
        .map(code => ({ code, name: this.platformLabel(code) }))
        .sort((a, b) => a.name.localeCompare(b.name, 'zh-CN'))
    },
    filteredEndpointOptions () {
      if (!this.recordFilter.platformCode) return this.endpoints
      return this.endpoints.filter(item => item.platformCode === this.recordFilter.platformCode)
    }
  },
  mounted () {
    this.loadAll()
  },
  methods: {
    async loadAll () {
      this.accessError = ''
      await Promise.all([
        this.loadCallers(true),
        this.loadPartners(true),
        this.loadEndpoints(true)
      ])
    },
    handleAccessError (error, silent) {
      const message = error.message || '运营代理管理数据加载失败'
      if (/管理权限|ADMIN_FORBIDDEN/.test(message)) {
        this.accessError = '请在服务端的 tikhub.operator.admin-user-ids 中加入当前登录用户ID后重试。'
      } else if (!silent) {
        this.$message.error(message)
      }
    },
    async loadCallers (silent) {
      this.callerLoading = true
      try {
        const params = {}
        if (this.callerFilter.partnerId) params.partner_id = this.callerFilter.partnerId
        if (typeof this.callerFilter.enabled === 'boolean') params.enabled = this.callerFilter.enabled
        this.callers = (await yuqingApi.get(`${ADMIN_BASE}/callers`, params)).items || []
      } catch (error) {
        this.callers = []
        this.handleAccessError(error, silent === true)
      } finally {
        this.callerLoading = false
      }
    },
    async loadPartners (silent) {
      this.partnerLoading = true
      try {
        const params = {}
        if (this.partnerDateRange && this.partnerDateRange.length === 2) {
          params.started_after = this.partnerDateRange[0]
          params.started_before = this.partnerDateRange[1]
        }
        this.partnerSummary = (await yuqingApi.get(
          `${ADMIN_BASE}/partners/usage-summary`,
          params
        )).items || []
      } catch (error) {
        this.partnerSummary = []
        this.handleAccessError(error, silent === true)
      } finally {
        this.partnerLoading = false
      }
    },
    async loadRecords () {
      this.recordLoading = true
      try {
        const params = { limit: 200 }
        Object.keys(this.recordFilter).forEach(key => {
          const value = this.recordFilter[key]
          if (value) {
            params[({
              userId: 'user_id',
              partnerId: 'partner_id',
              platformCode: 'platform_code',
              operationCode: 'operation_code',
              resultStatus: 'result_status'
            })[key]] = value
          }
        })
        if (this.recordDateRange && this.recordDateRange.length === 2) {
          params.started_after = this.recordDateRange[0]
          params.started_before = this.recordDateRange[1]
        }
        this.records = (await yuqingApi.get(`${ADMIN_BASE}/call-records`, params)).items || []
      } catch (error) {
        this.records = []
        this.handleAccessError(error, false)
      } finally {
        this.recordLoading = false
      }
    },
    async loadEndpoints (silent) {
      this.endpointLoading = true
      try {
        this.endpoints = (await yuqingApi.get(`${ADMIN_BASE}/endpoints`)).items || []
      } catch (error) {
        this.endpoints = []
        this.handleAccessError(error, silent === true)
      } finally {
        this.endpointLoading = false
      }
    },
    openCreateCaller () {
      this.editingCallerId = null
      this.callerForm = emptyCaller()
      this.callerDialogVisible = true
    },
    openEditCaller (row) {
      this.editingCallerId = row.id
      this.callerForm = {
        userId: row.userId,
        userName: row.userName,
        partnerId: row.partnerId,
        partnerName: row.partnerName,
        authorizedPlatforms: [...(row.authorizedPlatforms || [])],
        dailyLimit: this.number(row.dailyLimit),
        totalLimit: this.number(row.totalLimit),
        enabled: Boolean(row.enabled)
      }
      this.callerDialogVisible = true
    },
    resetCallerForm () {
      this.editingCallerId = null
      this.callerForm = emptyCaller()
      if (this.$refs.callerForm) this.$refs.callerForm.clearValidate()
    },
    saveCaller () {
      this.$refs.callerForm.validate(async valid => {
        if (!valid || this.callerSaving) return
        this.callerSaving = true
        try {
          const payload = {
            user_name: this.callerForm.userName,
            partner_id: this.callerForm.partnerId,
            partner_name: this.callerForm.partnerName,
            enabled: this.callerForm.enabled,
            authorized_platforms: this.callerForm.authorizedPlatforms,
            daily_limit: this.callerForm.dailyLimit,
            total_limit: this.callerForm.totalLimit
          }
          if (this.editingCallerId) {
            await yuqingApi.put(`${ADMIN_BASE}/callers/${this.editingCallerId}`, payload)
          } else {
            payload.user_id = this.callerForm.userId
            await yuqingApi.post(`${ADMIN_BASE}/callers`, payload)
          }
          this.$message.success(this.editingCallerId ? '运营账号权限已更新' : '运营账号已创建')
          this.callerDialogVisible = false
          await Promise.all([this.loadCallers(true), this.loadPartners(true)])
        } catch (error) {
          this.$message.error(error.message || '运营账号保存失败')
        } finally {
          this.callerSaving = false
        }
      })
    },
    resetQuota (row) {
      this.$prompt(
        `将“${row.userName}”的累计已用次数清零，每日用量不会清零。`,
        '重置累计额度',
        {
          confirmButtonText: '确认重置',
          cancelButtonText: '取消',
          inputPlaceholder: '填写重置原因（选填）',
          inputValidator: value => !value || value.length <= 500 || '重置原因不能超过500字'
        }
      ).then(async ({ value }) => {
        try {
          await yuqingApi.post(`${ADMIN_BASE}/callers/${row.id}/quota/reset`, {
            reason: value || ''
          })
          this.$message.success('累计额度已重置')
          await Promise.all([this.loadCallers(true), this.loadPartners(true)])
        } catch (error) {
          this.$message.error(error.message || '额度重置失败')
        }
      }).catch(() => undefined)
    },
    setPartnerEnabled (row, enabled) {
      const action = enabled ? '启用' : '停用'
      this.$confirm(
        `${action}伙伴“${row.partnerName}”后，将批量${action}该伙伴下全部运营账号。`,
        `${action}伙伴`,
        {
          confirmButtonText: `确认${action}`,
          cancelButtonText: '取消',
          type: enabled ? 'info' : 'warning'
        }
      ).then(async () => {
        try {
          const result = await yuqingApi.post(
            `${ADMIN_BASE}/partners/${encodeURIComponent(row.partnerId)}/enabled`,
            { enabled }
          )
          this.$message.success(`已${action}${this.number(result.updatedCount)}个运营账号`)
          await Promise.all([this.loadCallers(true), this.loadPartners(true)])
        } catch (error) {
          this.$message.error(error.message || `${action}伙伴失败`)
        }
      }).catch(() => undefined)
    },
    async updateEndpoint (row, payload) {
      if (this.endpointSaving) return
      this.endpointSaving = row.operationCode
      try {
        const result = await yuqingApi.put(
          `${ADMIN_BASE}/endpoints/${encodeURIComponent(row.operationCode)}/status`,
          payload
        )
        const updated = result.endpoint || {}
        Object.assign(row, updated)
        this.$message.success('代理接口状态已更新')
      } catch (error) {
        this.$message.error(error.message || '代理接口状态更新失败')
        await this.loadEndpoints(true)
      } finally {
        this.endpointSaving = ''
      }
    },
    usageText (used, reserved, limit) {
      const consumed = this.number(used)
      const pending = this.number(reserved)
      const maximum = this.number(limit)
      return `${consumed}${pending ? ` +${pending}预占` : ''} / ${maximum || '不限'}`
    },
    number (value) {
      const result = Number(value)
      return Number.isFinite(result) ? result : 0
    },
    platformLabel (code) {
      return PLATFORM_NAMES[code] || code || '-'
    },
    resultLabel (status) {
      return ({ SUCCESS: '成功', FAILED: '失败', TIMEOUT: '超时' })[status] || status || '-'
    },
    resultType (status) {
      return ({ SUCCESS: 'success', FAILED: 'danger', TIMEOUT: 'warning' })[status] || 'info'
    },
    liveStatusLabel (status) {
      return ({
        LIVE_VERIFIED: '验证通过',
        LIVE_FAILED: '验证失败',
        PENDING: '待验证'
      })[status] || status || '-'
    },
    liveStatusType (status) {
      return ({
        LIVE_VERIFIED: 'success',
        LIVE_FAILED: 'danger',
        PENDING: 'warning'
      })[status] || 'info'
    },
    dateTime (value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return String(value)
      const pad = number => String(number).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
        `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    }
  }
}
</script>

<style scoped>
.operator-settings {
  min-height: 360px;
}

.access-alert {
  margin: 8px 0;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(140px, 1fr));
  gap: 12px;
  margin-bottom: 18px;
}

.summary-item {
  padding: 14px 16px;
  border: 1px solid #ebeef5;
  background: #fafafa;
}

.summary-label {
  color: #909399;
  font-size: 12px;
}

.summary-value {
  margin-top: 6px;
  color: #303133;
  font-size: 24px;
  font-weight: 600;
  line-height: 30px;
}

.operator-tabs {
  min-height: 300px;
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin: 4px 0 16px;
}

.filters,
.record-filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.filter-partner {
  width: 190px;
}

.filter-status {
  width: 130px;
}

.date-range {
  width: 390px;
}

.record-filters {
  margin: 4px 0 16px;
}

.record-filters > .el-input,
.record-filters > .el-select {
  width: 170px;
}

.record-filters > .operation-filter {
  width: 210px;
}

.primary-text {
  color: #303133;
  font-weight: 500;
  line-height: 21px;
}

.secondary-text,
.form-help {
  color: #909399;
  font-size: 12px;
  line-height: 19px;
}

.platform-tag {
  margin: 2px 4px 2px 0;
}

.reserved {
  color: #e6a23c;
  font-size: 12px;
}

.danger-action {
  color: #f56c6c;
}

.endpoint-note {
  margin: 4px 0 16px;
}

.full-width {
  width: 100%;
}

@media (max-width: 1100px) {
  .summary-grid {
    grid-template-columns: repeat(2, minmax(140px, 1fr));
  }

  .toolbar {
    align-items: flex-start;
    flex-direction: column;
  }

  .date-range {
    width: 100%;
  }
}
</style>
